import React from "react";
import _ from "lodash";
import Select from "react-select";
import SimpleTooltip from "components/common/SimpleTooltip";

import { AppConfiguratorAPI } from "apis";

import TextAreaWithJsonCheck from "components/common/TextAreaWithJsonCheck";
import PromptModal from "components/common/PromptModal";
import CreateAppConfiguratorModal from "./CreateAppConfiguratorModal";
import LoadingUI from "components/common/LoadingUI";
import { EDITTYPE } from "./Constant";
import { MESSAGE } from "constants/Message";

import { FiPlusCircle } from "react-icons/fi";

const SWITCHOPTION = {
  APP: "app",
  CONTENT: "content",
  CREATE: "create",
};

class AppConfiguratorViewer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isOpenModal: false,
      isOpenCreateModal: false,
      isDisableSave: true,
      msg: [],

      apps: null,
      options: [],
      selectedApp: null,
      schema: null,
      defaults: null,
      editType: EDITTYPE.DEFAULTS,
      selectedConfig: null,
    };

    this.tempSelected = {
      option: null,
      value: null,
    };

    this.handleGetApps = this.handleGetApps.bind(this);
    this.handleAppSelect = this.handleAppSelect.bind(this);
    this.handleEditTypeChanged = this.handleEditTypeChanged.bind(this);
    this.handleCreateAppConfigurator =
      this.handleCreateAppConfigurator.bind(this);
    this.handleNewAppCreated = this.handleNewAppCreated.bind(this);
    this.handleConfigChanged = this.handleConfigChanged.bind(this);

    this.handelModalConfirm = this.handelModalConfirm.bind(this);
    this.handelModalCancel = this.handelModalCancel.bind(this);

    this.handleSave = this.handleSave.bind(this);
  }

  async componentDidMount() {
    this.handleGetApps();
  }

  async handleGetApps() {
    this.setState({ isLoading: true, msg: [] });

    try {
      let apps = await AppConfiguratorAPI.getApps();
      // show status in the future, only show RUNNING for now
      apps = _.filter(apps, (app) => app.status === "Running");

      if (apps && apps.length > 0) {
        const options = apps.map((app) => ({
          value: app.name,
          label: `${app.name}`,
          // label: `[${app.status}] ${app.name}`,
        }));
        this.setState({ apps, options });
      }
    } catch (err) {
      this.setState({ msg: [JSON.parse(err).error || err, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  async handleAppSelect(app, isForceExecution = false) {
    const { value } = app;
    const { selectedApp, isDisableSave } = this.state;

    if (selectedApp && selectedApp.value === value) return;

    if (!isDisableSave && !isForceExecution) {
      this.tempSelected.option = SWITCHOPTION.APP;
      this.tempSelected.value = app;
      this.setState({ isOpenModal: true });
      return;
    }

    this.setState({
      selectedApp: app,
      isLoading: true,
      msg: [],
    });

    try {
      const result = await AppConfiguratorAPI.getAppSchemaAndDefaultValues({
        appName: value,
      });
      if (result) {
        const { schema, defaults } = result;
        this.setState(
          {
            schema,
            defaults,
            isDisableSave: true,
          },
          () => {
            this.handleEditTypeChanged(this.state.editType, true);
          }
        );
      }
    } catch (err) {
      this.setState({ msg: [JSON.parse(err).error || err, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  handleEditTypeChanged(type, isForceExecution = false) {
    const { selectedApp, editType, isDisableSave, schema } = this.state;

    if (!isForceExecution && editType === type) return;

    if (!isDisableSave && !isForceExecution) {
      this.tempSelected.option = SWITCHOPTION.CONTENT;
      this.tempSelected.value = type;
      this.setState({ isOpenModal: true });
      return;
    }

    this.setState({ editType: type });
    if (!selectedApp) return;

    const config =
      type === EDITTYPE.DEFAULTS ? this.state.defaults : this.state.schema;
    this.setState({
      isDisableSave: true,
      msg: _.isEmpty(schema)
        ? ["Please set the schema first.", MESSAGE.WARNING]
        : [],
      selectedConfig: JSON.stringify(config, null, 4),
    });
  }

  handleCreateAppConfigurator(isForceExecution = false) {
    const { isDisableSave } = this.state;

    if (!isDisableSave && !isForceExecution) {
      this.tempSelected.option = SWITCHOPTION.CREATE;
      this.tempSelected.value = null;
      this.setState({ isOpenModal: true });
      return;
    }

    this.handleEditTypeChanged(this.state.editType, true);
    this.setState({
      isOpenCreateModal: true,
    });
  }

  async handleNewAppCreated(app) {
    this.setState({ isOpenCreateModal: false });
    await this.handleGetApps();

    const { options } = this.state;
    const newAppOption = options.find((option) => option.value === app);
    if (newAppOption) {
      this.setState(
        {
          editType: EDITTYPE.SCHEMA,
        },
        () => {
          this.handleAppSelect(newAppOption, true);
        }
      );
    }
  }

  handleConfigChanged(input) {
    try {
      const { editType, schema, defaults } = this.state;
      this.setState({ selectedConfig: input });
      const config = JSON.parse(input);
      const originalConfig = editType === EDITTYPE.DEFAULTS ? defaults : schema;
      this.setState({ isDisableSave: _.isEqual(originalConfig, config) });
    } catch (e) {
      this.setState({ isDisableSave: true });
    }
  }

  handelModalConfirm() {
    const { option, value } = this.tempSelected;

    if (option === SWITCHOPTION.APP) {
      this.handleAppSelect(value, true);
    } else if (option === SWITCHOPTION.CONTENT) {
      this.handleEditTypeChanged(value, true);
    } else if (option === SWITCHOPTION.CREATE) {
      this.handleCreateAppConfigurator(true);
    }

    this.tempSelected = {
      option: null,
      value: null,
    };
    this.setState({ isOpenModal: false });
  }

  handelModalCancel() {
    this.setState({ isOpenModal: false });
  }

  async handleSave() {
    const { selectedApp, editType, selectedConfig } = this.state;
    const config = JSON.parse(selectedConfig);
    this.setState({ isLoading: true, msg: [] });

    try {
      const result =
        editType === EDITTYPE.DEFAULTS
          ? await AppConfiguratorAPI.createOrUpdateAppDefaultValues({
              appName: selectedApp.value,
              defaultValues: config,
            })
          : await AppConfiguratorAPI.createOrUpdateAppSchema({
              appName: selectedApp.value,
              configSchema: config,
            });
      if (result && result.op === "ok") {
        this.setState({
          msg: ["Saved successfully", MESSAGE.SUCCESS],
        });
        if (editType === EDITTYPE.DEFAULTS) this.setState({ defaults: config });
        else this.setState({ schema: config });
        this.setState({ isDisableSave: true });
      } else {
        this.setState({
          msg: ["Failed to save. Error: no result response", MESSAGE.ERROR],
        });
      }
    } catch (err) {
      this.setState({
        msg: [`Failed to save. ${JSON.parse(err).error || err}`, MESSAGE.ERROR],
      });
    }

    this.setState({ isLoading: false });
  }

  render() {
    const {
      isDisableSave,
      isOpenModal,
      isOpenCreateModal,
      isLoading,
      apps,
      options,
      editType,
      selectedConfig,
      selectedApp,
      msg,
    } = this.state;

    return (
      <>
        <div className="bg-blue-900 px-2">
          <div className="text-sm font-bold text-blue-100">&nbsp;</div>
        </div>
        <div className="bg-white px-12">
          <div className="flex justify-between pb-4 pt-8 ">
            <div className="text-4xl font-extrabold text-gray-900">
              App Configurator Management
            </div>
          </div>
        </div>
        <div
          className="relative overflow-y-auto bg-gray-200 px-12 py-8"
          style={{ height: "calc(100% - 7.6875rem)" }}
        >
          <label className="flex items-center gap-2 font-semibold text-gray-800">
            Select App
            <SimpleTooltip tooltipText="Create App">
              <div
                className="rounded flex cursor-pointer items-center bg-gray-100 text-lg text-blue-600 hover:text-blue-500"
                onClick={() => this.handleCreateAppConfigurator()}
              >
                <FiPlusCircle></FiPlusCircle>
              </div>
            </SimpleTooltip>
          </label>
          <div className="mb-4 flex items-center gap-2">
            <Select
              className="w-1/4"
              value={selectedApp}
              onChange={(value) => this.handleAppSelect(value)}
              options={options}
            ></Select>
          </div>
          <div className="mb-2 flex flex-wrap gap-2">
            {[EDITTYPE.DEFAULTS, EDITTYPE.SCHEMA].map((item) => (
              <div
                key={item}
                className={`cursor-pointer rounded-md px-3 py-1 ${
                  editType === item
                    ? "bg-blue-400 text-gray-100"
                    : "bg-blue-100 text-black hover:bg-blue-200"
                }`}
                onClick={() => this.handleEditTypeChanged(item)}
              >
                {_.startCase(item)}
              </div>
            ))}
          </div>
          <div className="relative pb-2">
            {selectedApp && (
              <TextAreaWithJsonCheck
                rows={15}
                iconOnly={true}
                inputValue={selectedConfig}
                handleInputChanged={this.handleConfigChanged}
              ></TextAreaWithJsonCheck>
            )}
            <div
              className="absolute flex w-1/2 items-center justify-end gap-2"
              style={{ top: "24rem", right: "0rem" }}
            >
              {msg && msg.length > 0 && (
                <div
                  className={`rounded flex-1 break-words px-4 py-2 text-left font-semibold
                  ${
                    msg[1] === MESSAGE.SUCCESS
                      ? "bg-green-200 text-green-600"
                      : ""
                  }
                  ${msg[1] === MESSAGE.ERROR ? "bg-red-200 text-red-600" : ""}
                  ${
                    msg[1] === MESSAGE.WARNING
                      ? "bg-orange-200 text-orange-600"
                      : ""
                  }
                  `}
                >
                  {msg[0]}
                </div>
              )}
              {selectedApp && (
                <button
                  type="button"
                  className={`shadow rounded px-8 py-2 font-semibold text-white  ${
                    isDisableSave
                      ? "cursor-not-allowed bg-blue-200"
                      : "bg-blue-400 hover:bg-blue-600"
                  }`}
                  disabled={isDisableSave}
                  onClick={this.handleSave}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>

        <PromptModal
          header="Unsaved Changes Alert"
          isOpenModal={isOpenModal}
          handleConfirm={this.handelModalConfirm}
          handleCancel={this.handelModalCancel}
        >
          You have unsaved changes. If you switch now, these changes will be
          lost. <br />
          Do you want to proceed?
        </PromptModal>

        {isOpenCreateModal && (
          <CreateAppConfiguratorModal
            apps={apps}
            onCancel={() => this.setState({ isOpenCreateModal: false })}
            onClose={(value) => this.handleNewAppCreated(value)}
          ></CreateAppConfiguratorModal>
        )}

        {isLoading && (
          <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-gray-800 opacity-25">
            <LoadingUI iconOnly={true}></LoadingUI>
          </div>
        )}
      </>
    );
  }
}

export default AppConfiguratorViewer;
